/**
 * Helpers for classifying a project by its `projectType` and deriving
 * type-specific UI labels.
 *
 * Construction / E&C projects use industry-standard terminology that
 * differs from aerospace/government EVM language. Specifically:
 *   - "Baseline Management" (PMB / EVM term) → "Budget Management" in E&C.
 *
 * Centralize the classification + label here so the same rule fires
 * everywhere the term appears (sidebar, breadcrumb, command palette,
 * page header, help panels, and project tile tooltips).
 */

/** Project-type strings the Direct startup wizard offers, plus a few
 *  synonyms that show up in seed data and older imports. Lowercased. */
const EC_TYPE_TOKENS = [
  'engineering & construction direct',
  'epc (engineer, procure, construct)',
  'epcm (engineering, procurement, construction management)',
  'design-build',
  'construction management at risk',
  'general contracting',
  'renovation / retrofit',
  'commissioning & startup',
  "owner's engineer / pmc",
]

/** Keyword fallback so projects with bespoke type strings still match. */
const EC_KEYWORDS = ['construction', 'epc', 'epcm', 'design-build', 'renovation', 'retrofit']

export function isEngineeringConstructionProject(projectType: string | null | undefined): boolean {
  if (!projectType) return false
  const t = String(projectType).toLowerCase().trim()
  if (!t) return false
  if (EC_TYPE_TOKENS.includes(t)) return true
  return EC_KEYWORDS.some((kw) => t.includes(kw))
}

/** Label for the `/finance` page (sidebar, page header, breadcrumb, etc.). */
export function getFinancePageLabel(projectType: string | null | undefined): string {
  return isEngineeringConstructionProject(projectType) ? 'Budget Management' : 'Baseline Management'
}

/**
 * Should the Startup Checklist be offered for this project?
 *
 * The 22-step PRQ/DCE checklist is a construction / E&C startup gate, so by
 * default it only shows on those projects, and only while they are in Startup
 * status. Instances that want it everywhere flip the
 * `startupChecklist.allProjectTypes` capability in Configuration > Capabilities.
 */
export function isStartupChecklistApplicable(
  project: { status?: string | null; projectType?: string | null } | null | undefined,
  allProjectTypes: boolean,
): boolean {
  if (!project) return false
  if (project.status !== 'Startup') return false
  return allProjectTypes || isEngineeringConstructionProject(project.projectType)
}
